export interface ImgproxyUploadMetadata {
    DateTimeOriginal?: string | Date | null;
    SubsecTimeOriginal?: string | null;
    CreationDate?: string | Date | null;
    CreateDate?: string | Date | null;
}

interface ImgproxyInfoResponse {
    exif?: Record<string, unknown>;
    iptc?: Record<string, unknown>;
    photoshop?: Record<string, unknown>;
    xmp?: Record<string, unknown>;
    video_meta?: Record<string, unknown>;
}

const exifDatePattern = /^([0-9]{4}):([0-9]{2}):([0-9]{2}) ([0-9]{2}):([0-9]{2}):([0-9]{2})/i;
const placeholderDatePattern = /^(0000[:-]00[:-]00|1904[:-]01[:-]01)\b/;

function getImgproxyBaseUrl(s3Bucket: string) {
    return s3Bucket.toLowerCase().startsWith('dev-')
        ? 'https://img.dev.dealervision.com'
        : 'https://img.dealervision.com';
}

function getImgproxyBucketAlias(s3Bucket: string) {
    const normalizedBucket = s3Bucket.toLowerCase();

    if (normalizedBucket.endsWith('useruploads') || normalizedBucket.endsWith('user-uploads')) {
        return 'user-uploads';
    }

    return normalizedBucket;
}

function encodeS3Path(s3Path: string) {
    return s3Path
        .replace(/^\/+/, '')
        .split('/')
        .map(segment => encodeURIComponent(segment))
        .join('/');
}

function firstValue(...values: unknown[]) {
    return values.find(value => value !== undefined && value !== null && value !== '') as string | Date | undefined;
}

function pathValue(source: Record<string, unknown>, path: string | string[]) {
    if (typeof path === 'string') {
        return source[path];
    }

    return path.reduce((current: unknown, segment) => {
        if (current == null || typeof current !== 'object') {
            return undefined;
        }

        return (current as Record<string, unknown>)[segment];
    }, source as unknown);
}

function firstPathValue(source: Record<string, unknown>, ...paths: Array<string | string[]>) {
    return firstValue(...paths.map(path => pathValue(source, path)));
}

function normalizeOffset(offset: unknown) {
    if (!offset) {
        return undefined;
    }

    const offsetText = String(offset).trim();
    const offsetMatch = /^([+-])([0-9]{2}):?([0-9]{2})$/.exec(offsetText);

    return offsetMatch == null ? undefined : `${offsetMatch[1]}${offsetMatch[2]}:${offsetMatch[3]}`;
}

function localOffsetString() {
    const tzOffset = new Date().getTimezoneOffset();
    const tzSign = (tzOffset <= 0) ? '+' : '-';
    const tzOffsetHours = Math.floor(Math.abs(tzOffset) / 60).toString().padStart(2, '0');
    const tzOffsetMin = (Math.abs(tzOffset) % 60).toString().padStart(2, '0');

    return `${tzSign}${tzOffsetHours}:${tzOffsetMin}`;
}

function exifDateWithOffset(dateValue: unknown, offsetValue: unknown) {
    const offset = normalizeOffset(offsetValue);

    if (!dateValue || !offset) {
        return dateValue as string | Date | undefined;
    }

    const dateMatch = exifDatePattern.exec(String(dateValue));

    if (dateMatch == null) {
        return dateValue as string | Date | undefined;
    }

    return `${dateMatch[1]}-${dateMatch[2]}-${dateMatch[3]}T${dateMatch[4]}:${dateMatch[5]}:${dateMatch[6]}${offset}`;
}

function parseMetadataDate(value: unknown) {
    if (!value) {
        return null;
    }

    if (value instanceof Date) {
        return value.getTime();
    }

    const dateText = String(value);
    if (placeholderDatePattern.test(dateText)) {
        return null;
    }

    const dateMatch = exifDatePattern.exec(dateText);
    const parseableDate = dateMatch == null
        ? dateText
        : `${dateMatch[1]}-${dateMatch[2]}-${dateMatch[3]}T${dateMatch[4]}:${dateMatch[5]}:${dateMatch[6]}${localOffsetString()}`;
    const parsedDate = Date.parse(parseableDate);

    return Number.isNaN(parsedDate) ? null : parsedDate;
}

function earliestDateValue(...values: unknown[]) {
    let earliestValue: string | Date | undefined = undefined;
    let earliestTime = Number.POSITIVE_INFINITY;

    for (const value of values) {
        const parsedDate = parseMetadataDate(value);

        if (parsedDate != null && parsedDate < earliestTime) {
            earliestValue = value as string | Date;
            earliestTime = parsedDate;
        }
    }

    return earliestValue;
}

function combineDateAndTime(dateValue: unknown, timeValue: unknown) {
    if (!dateValue || !timeValue) {
        return dateValue as string | Date | undefined;
    }

    const dateText = String(dateValue).replace(/^([0-9]{4})-([0-9]{2})-([0-9]{2})$/, '$1:$2:$3');

    return `${dateText} ${timeValue}`;
}

function normalizeImgproxyInfo(info: ImgproxyInfoResponse): ImgproxyUploadMetadata {
    const exif = info.exif ?? {};
    const iptc = info.iptc ?? {};
    const photoshop = info.photoshop ?? {};
    const xmp = info.xmp ?? {};
    const videoMeta = info.video_meta ?? {};
    const dateTimeOriginal = exifDateWithOffset(
        firstPathValue(
            exif,
            'DateTimeOriginal',
            'Date and Time (Original)',
            'Date/Time Original'
        ),
        firstPathValue(
            exif,
            'OffsetTimeOriginal',
            'Offset Time For DateTimeOriginal',
            'Offset Time Original'
        )
    );
    const createDate = exifDateWithOffset(
        firstPathValue(
            exif,
            'CreateDate',
            'DateTimeDigitized',
            'DateTime',
            'Date and Time (Digitized)',
            'Date and Time'
        ),
        firstPathValue(
            exif,
            'OffsetTimeDigitized',
            'OffsetTime',
            'Offset Time For DateTimeDigitized',
            'Offset Time For DateTime'
        )
    );

    return {
        DateTimeOriginal: firstValue(
            dateTimeOriginal,
            xmp.DateTimeOriginal,
            firstPathValue(xmp, ['exif', 'DateTimeOriginal'])
        ),
        SubsecTimeOriginal: firstValue(
            exif.SubsecTimeOriginal,
            exif.SubSecTimeOriginal,
            exif['Sub-second Time (Original)'],
            xmp.SubsecTimeOriginal,
            xmp.SubSecTimeOriginal
        ) as string | null | undefined,
        CreationDate: earliestDateValue(
            exif.CreationDate,
            exif['Creation Date'],
            xmp.CreationDate,
            xmp['Creation Date'],
            firstPathValue(xmp, ['photoshop', 'DateCreated']),
            photoshop.DateCreated,
            photoshop['Date Created'],
            combineDateAndTime(iptc.DateCreated, iptc.TimeCreated),
            combineDateAndTime(iptc['Date Created'], iptc['Time Created']),
            videoMeta['com.apple.quicktime.creationdate'],
            videoMeta['com.apple.quicktime.creationDate'],
            videoMeta['com.apple.quicktime.creation-date'],
            videoMeta.CreationDate,
            videoMeta['Creation Date'],
            videoMeta.MediaCreateDate,
            videoMeta['MediaCreateDate'],
            videoMeta['Media Create Date'],
            videoMeta.media_create_date,
            videoMeta.creation_time
        ),
        CreateDate: earliestDateValue(
            createDate,
            xmp.CreateDate,
            xmp['Create Date'],
            firstPathValue(xmp, ['xmp', 'CreateDate']),
            videoMeta.CreateDate,
            videoMeta['Create Date'],
            videoMeta.TrackCreateDate,
            videoMeta['Track Create Date'],
            videoMeta.track_create_date
        )
    };
}

export async function getImgproxyUploadMetadata(s3Bucket: string, s3Path: string): Promise<ImgproxyUploadMetadata> {
    const imgproxyUrl = `${getImgproxyBaseUrl(s3Bucket)}/@${getImgproxyBucketAlias(s3Bucket)}/_info/${encodeS3Path(s3Path)}`;
    const response = await fetch(imgproxyUrl);

    if (!response.ok) {
        throw new Error(`imgproxy info request failed with HTTP ${response.status}`);
    }

    return normalizeImgproxyInfo(await response.json());
}
